import throttle from "lodash/throttle";
import {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import {
  sendEventData,
  generateZephrTrackingId,
  getZephrExperimentsReadyEvent,
} from "@framework/entitlements/entitlements.utils";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import {
  useIsEntitled,
  useUserId,
} from "@voxmedia/duet-context/context/auth-context";
import { PaywallConfig } from "@voxmedia/duet-context/context/config-context";
import { getCookie } from "@voxmedia/duet-utils/cookie";
import useIsomorphicLayoutEffect from "@voxmedia/duet-utils/use-isomorphic-layout-effect";

// Define constants for event action types to improve readability
const EVENT_ACTIONS = {
  PAGE_IMPRESSION: 21,
  GROWL_TOUT_IMPRESSION: 41,
  CLIFF_TOUT_IMPRESSION: 44,
  TOUT_CLICK: 28,
  SCROLL: 46,
};

const ZEPHR_COOKIE_NAME = "blaize_tracking_id";

// Define scroll milestone percentages
const SCROLL_MILESTONES = [30, 60, 90] as const;
type ScrollMilestone = (typeof SCROLL_MILESTONES)[number];

interface Props {
  isPaywallEligible: boolean;
  paywallConfig: PaywallConfig;
}

const SubX: FunctionComponent<Props> = (props) => {
  const { isPaywallEligible, paywallConfig } = props;
  const hasSubscription = useIsEntitled();
  const userId = useUserId();
  const analytics = useAnalytics();

  const [initializedData, setInitializedData] = useState<
    Record<string, unknown>
  >({});
  const [zephrData, setZephrData] =
    useState<Record<string, { usedInDecision: boolean } | undefined>>();
  // Use a single state object to track scroll milestones instead of multiple booleans
  const [scrollMilestones, setScrollMilestones] = useState<
    Record<ScrollMilestone, boolean>
  >({ 30: false, 60: false, 90: false });

  // Use refs for DOM elements and flags to avoid unnecessary re-renders
  const isListeningRef = useRef(false);
  const toutElementRef = useRef<Element | null>(null);
  const anchorElementRef = useRef<Element | null>(null);
  const dataLayerInitialized = useRef(false);

  // Cache common data that doesn't change during component lifecycle
  const commonEventData = useRef<Record<string, unknown>>({});

  // ensures that the dataLayer-initalized event is available before pulling info
  useIsomorphicLayoutEffect(() => {
    if (window.dataLayer?.push && !dataLayerInitialized.current) {
      const originalPush = window.dataLayer.push;

      // overriding the window.dataLayer.push event to fire a listenable DataLayerInitEvent
      window.dataLayer.push = (...args: Record<string, string>[]) => {
        const states = [].slice.call(args, 0);

        switch (args[0]?.event) {
          case "dataLayer-initalized":
            window.dispatchEvent(
              new CustomEvent("DataLayerInitEvent", { detail: args[0] }),
            );
            break;
          default:
        }

        return originalPush.apply(window.dataLayer, states);
      };

      const handleDataLayerInit = (
        e: CustomEventInit<Record<string, unknown>>,
      ) => {
        setInitializedData(e.detail || {});
      };

      const handleZephrInit = () => {
        setZephrData(window.Zephr?.accessDetails?.trials || {});
      };

      window.addEventListener("DataLayerInitEvent", handleDataLayerInit, {
        once: true,
      });

      // this event is fired from zephr-browser.umd.js
      document.addEventListener(
        "zephr.browserDecisionsFinished",
        handleZephrInit,
        {
          once: true,
        },
      );

      commonEventData.current = {
        userAgent: window.navigator.userAgent,
        countryCode: navigator.language.split("-")[1],
        referSource: document.referrer,
      };

      // Cleanup function to restore original push method
      return () => {
        if (window.dataLayer) {
          window.dataLayer.push = originalPush;
        }
        window.removeEventListener("DataLayerInitEvent", handleDataLayerInit);
        document.addEventListener(
          "zephr.browserDecisionsFinished",
          handleZephrInit,
          {
            once: true,
          },
        );
      };
    }
  }, []);

  // Memoize the user state calculation
  const getUserState = useCallback(() => {
    if (!userId) {
      return "anonymous";
    } else if (hasSubscription) {
      return "subscriber";
    } else {
      return "registered";
    }
  }, [userId, hasSubscription]);

  // Create a memoized function to get the tout on the page, if available
  const getTout = useCallback(() => {
    if (!toutElementRef.current) {
      toutElementRef.current = document.querySelector(
        "#zephr-footer-container",
      );
    }
    return toutElementRef.current;
  }, []);

  // Create a memoized function to get the zephr anchor on the page, if this page is eligible for zephr decisions
  const getAnchor = useCallback(() => {
    if (!anchorElementRef.current) {
      anchorElementRef.current = document.querySelector("#zephr-anchor");
    }
    return anchorElementRef.current;
  }, []);

  const checkTrackingIdCookie = async () => {
    const trackingId = getCookie(ZEPHR_COOKIE_NAME);
    if (!trackingId) {
      await generateZephrTrackingId();
    }
  };

  // Create a base payload factory to reduce duplication
  const createBasePayload = useCallback(
    async (action: number, actionValue?: string) => {
      const tout = getTout();
      const categories = (
        (initializedData["All Categories"] as string) || ""
      ).split(",");
      const articleType = (initializedData["Page Type"] as string) || "";
      const excludedArticleTypes = paywallConfig.excludedArticleTypes || [];
      const excludedCategories = paywallConfig.meterExceptions || [];
      const isExcludedByCategory = categories.some((category) =>
        excludedCategories.includes(category),
      );
      const isExcludedByType = excludedArticleTypes.includes(articleType);
      const increaseRA = !isExcludedByCategory && !isExcludedByType;
      const wid =
        action !== EVENT_ACTIONS.PAGE_IMPRESSION
          ? tout?.getAttribute("data-wid")
          : "";

      // sets a Zephr tracking id if there is not one set
      await checkTrackingIdCookie();

      return {
        wId_2: actionValue ? undefined : [wid],
        dtId: getCookie(ZEPHR_COOKIE_NAME),
        action,
        actionValue,
        refer: document.referrer,
        requestUrl: window.location.href,
        user: { userId: userId || "", userState: getUserState() },
        ...(actionValue
          ? {}
          : {
              content: {
                section: initializedData["Primary Category"],
                subSection: categories,
                articleId: initializedData["Page ID"],
                articleType,
                articleClass: isPaywallEligible ? "paywall eligible" : "free",
                increaseRA,
              },
              context: {
                custom: { variable_1: initializedData["Headline"] },
                ...commonEventData.current,
              },
            }),
        ...(actionValue ? { context: commonEventData.current } : {}),
      };
    },
    [
      initializedData,
      isPaywallEligible,
      getUserState,
      getTout,
      paywallConfig.meterExceptions,
      paywallConfig.excludedArticleTypes,
      userId,
    ],
  );

  const hasAllData = useCallback(() => {
    const waitingForZephr = getAnchor() && !zephrData; // we aren't waiting for zephr if the anchor isn't on the page

    return Object.entries(initializedData).length > 0 && !waitingForZephr;
  }, [initializedData, zephrData, getAnchor]);

  const trackEvent = useCallback(
    async (action: number) => {
      void sendEventData(await createBasePayload(action));
    },
    [createBasePayload],
  );

  // tracks page impression
  useEffect(() => {
    if (hasAllData()) {
      void trackEvent(EVENT_ACTIONS.PAGE_IMPRESSION);
    }
  }, [hasAllData, trackEvent, analytics]);

  // tracks Zephr experiments for GA
  useEffect(() => {
    async function emitAnalytics() {
      // TODO: consider moving this to another global component, but for now this should work
      if (analytics) {
        analytics.emit(await getZephrExperimentsReadyEvent());
      }
    }

    if (hasAllData() && analytics) {
      void emitAnalytics();
    }
  }, [hasAllData, analytics]);

  // tracks tout impressions and clicks
  useEffect(() => {
    if (hasAllData()) {
      const tout = getTout();
      // only attach the click listener once the tout is displayed
      if (tout) {
        let impressionValue = EVENT_ACTIONS.CLIFF_TOUT_IMPRESSION;

        if (tout.classList.contains("duet--growl")) {
          impressionValue = EVENT_ACTIONS.GROWL_TOUT_IMPRESSION;
        }

        const handleImpression = () => {
          void trackEvent(impressionValue);
        };
        const handleClick = () => {
          void trackEvent(EVENT_ACTIONS.TOUT_CLICK);
        };

        if (tout.classList.contains("visible")) {
          handleImpression();
          tout.addEventListener("click", handleClick);
          return () => {
            tout.removeEventListener("click", handleClick);
          };
        } else {
          tout.addEventListener("ToutImpression", handleImpression);
          tout.addEventListener("click", handleClick);
          return () => {
            tout.removeEventListener("ToutImpression", handleImpression);
            tout.removeEventListener("click", handleClick);
          };
        }
      }
    }
  }, [hasAllData, getTout, trackEvent]);

  // sends a scroll event once a user scrolls 30% / 60% / 90% down a page
  const trackScroll = useCallback(async () => {
    // async?
    if (isListeningRef.current) {
      return;
    }
    isListeningRef.current = true;

    // Calculate scroll percentage
    const scrollPercent =
      (100 * (window.scrollY + window.outerHeight)) /
      document.body.offsetHeight;

    // Check each milestone and send events for any that are newly reached
    for (const milestone of SCROLL_MILESTONES) {
      if (scrollPercent > milestone && !scrollMilestones[milestone]) {
        setScrollMilestones((prev) => ({ ...prev, [milestone]: true }));
        void sendEventData(
          await createBasePayload(EVENT_ACTIONS.SCROLL, String(milestone)),
        );
      }
    }

    isListeningRef.current = false;
  }, [scrollMilestones, createBasePayload]);

  // tracks article scroll depth for SubX
  useEffect(() => {
    // 200ms throttling to improve performance
    const throttledScrollHandler = throttle(() => {
      void trackScroll();
    }, 200);
    window.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", throttledScrollHandler);
    };
  }, [trackScroll]);

  return <></>;
};

export default SubX;
